import { useState, useEffect, lazy, createRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Navigate, Routes as RouterRoutes, Route } from 'react-router-dom';
import { getStaticPageTitle } from 'utils/Helpers';
import { useApi } from 'hooks/use-api/useApi';
import { useI18n } from 'utils/i18n/usei18n';
import { useUpdateState } from 'hooks/useUpdateState';
import { useSelector } from 'react-redux';
import { AppState } from 'store/index';
import { Constants } from 'utils/Constants';
import StartPage from 'pages/cms/StartPage';

const PreviewPage = lazy(() => import('pages/cms/PreviewPage'));
const ContentPage = lazy(() => import('pages/cms/ContentPage'));
const LandingPage = lazy(() => import('pages/cms/landing-page/LandingPage'));
const ProfileLanding = lazy(() => import('pages/user-profile/profile-landing/ProfileLanding'));
const ProfilePage = lazy(() => import('pages/user-profile/profile/ProfilePage'));
const ProfileDataPage = lazy(() => import('pages/user-profile/profile-data/ProfileDataPage'));
const ProfileDeletePage = lazy(() => import('pages/user-profile/delete-profile/ProfileDeletePage'));
const ChangePasswordPage = lazy(() => import('pages/user-profile/change-password/ChangePasswordPage'));
const PlannerPage = lazy(() => import('pages/planner/PlannerPage'));
const LoginProceedPage = lazy(() => import('pages/login-proceed/LoginProceedPage'));
const DepartureJourneyPage = lazy(() => import('pages/journey/DepartureJourneyPage'));
const ReturnJourneyPage = lazy(() => import('pages/journey/ReturnJourneyPage'));
const ReviewPage = lazy(() => import('pages/review/ReviewPage'));
const PaymentPage = lazy(() => import('pages/payment/PaymentPage'));
const NetsRedirectPage = lazy(() => import('pages/payment-redirect/NetsRedirectPage'));
const VippsRedirectPage = lazy(() => import('pages/payment-redirect/VippsRedirectPage'));
const MyJourneysPage = lazy(() => import('pages/user-profile/my-journeys/MyJourneysPage'));
const MyCardsPage = lazy(() => import('pages/user-profile/my-cards/MyCardsPage'));
const ResponsePage = lazy(() => import('pages/response/ResponsePage'));
const RealTimeView = lazy(() => import('components/real-time-view/RealTimeView'));
const BonusPage = lazy(() => import('pages/user-profile/bonus-page/BonusPage'));

type RoutesRenderProps = {
  routesLoading: boolean;
  cmsLinks: any[];
  headerLinks: any;
  footerLinks: any;
  onRouteChangeFocusRef: RefObject<HTMLElement>;
  routesElement: ReactNode;
};

export const Routes = ({ children }: { children: (props: RoutesRenderProps) => JSX.Element }): JSX.Element => {
  const { API_CALLS, requestCms } = useApi();
  const { translate } = useI18n();
  const { setTitle } = useUpdateState();
  const { pageTitle } = useSelector((store: AppState) => store.global);
  const onRouteChangeFocusRef = createRef<HTMLElement>();

  const [routesLoading, setRoutesLoading] = useState(true);
  const [cmsLinks, setCmsLinks] = useState([]);
  const [headerLinks, setHeaderLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [dynamicRoutes, setDynamicRoutes] = useState<any[]>([]);

  const fetchCmsLinks = async () => {
    let allInfoLinks;
    let pageSlugs;
    try {
      pageSlugs = allInfoLinks = await (await fetch('/sluglist.json')).json();
    } catch {
      try {
        allInfoLinks = await requestCms(API_CALLS.CMS_GET_CMS_LINKS);
        pageSlugs = await requestCms(API_CALLS.CMS_GET_PAGES_WITH_SLUGS);
      } catch {
        setCmsLinks([]);
        setRoutesLoading(false);
        return;
      }
    }

    const header = allInfoLinks
      ? allInfoLinks.filter((link) => link.tag && link.tag === Constants.SANITY.GOA_INFORMATIONAL_TAG)
      : [];

    const footer = allInfoLinks
      ? allInfoLinks.filter((link) => link.tag && link.tag !== Constants.SANITY.GOA_INFORMATIONAL_TAG)
      : [];

    const articleLanding = allInfoLinks
      ? allInfoLinks.filter((link) => link.apiId === Constants.SANITY.GOA_ARTICLE_LANDING_PAGE)
      : [];

    const articlePage = pageSlugs
      ? pageSlugs.filter((item) => item.apiId && item.apiId === Constants.SANITY.GOA_ARTICLE_PAGE)
      : [];

    const standardPage = pageSlugs
      ? pageSlugs.filter((item) => item.apiId && item.apiId === Constants.SANITY.GOA_STANDARD_PAGE)
      : [];

    standardPage.push({
      apiId: 'GOA_STANDARD_PAGE',
      slug: {
        _type: 'slug',
        current: 'cookie-policy',
      },
      title: 'Informasjonskapsler (cookies)',
    });

    setCmsLinks([...header, ...footer, ...articleLanding]);
    setHeaderLinks(header);
    setFooterLinks(footer);

    // Build dynamic routes from CMS links
    const routes: any[] = [];

    // Landing routes
    [...header, ...footer, ...articleLanding].forEach((element) => {
      routes.push({
        path: `/${element.slug.current}`,
        element: <LandingPage slug={element.slug.current} />,
      });
    });

    // Content routes
    [...header, ...footer, ...articleLanding, ...articlePage, ...standardPage].forEach((element) => {
      routes.push({
        path: `/${element.slug.current}/:slug`,
        element: <ContentPage slug={element.slug.current} />,
      });

      if (element.apiId === Constants.SANITY.GOA_ARTICLE_PAGE || element.apiId === Constants.SANITY.GOA_STANDARD_PAGE) {
        routes.push({
          path: `/${element.slug.current}`,
          element: <ContentPage slug={element.slug.current} />,
        });
      }
    });

    setDynamicRoutes(routes);
    setRoutesLoading(false);
  };

  useEffect(() => {
    //only handles title for predefined routes
    //dynamic routes from Cms will be set in respective component
    setTitle(getStaticPageTitle(window.location.pathname, translate));
  }, [window.location.pathname]);

  useEffect(() => {
    const url = window.document.URL;
    const showEnvironmentTag = url.includes('test.goalive.go-aheadnordic')
      ? 'D | '
      : url.includes('qa.go-ahead.no')
      ? 'S | '
      : '';

    const sTitle = `${pageTitle}`;

    window.document.title = `${showEnvironmentTag}${sTitle}`;
    onRouteChangeFocusRef.current?.focus();
  }, [pageTitle]);

  useEffect(() => {
    (async () => {
      await fetchCmsLinks();
    })();
  }, []);

  useEffect(() => {
    let focusedTitle;

    const handleFocus = () => {
      document.title = focusedTitle || `${pageTitle}`;
    };

    const handleBlur = () => {
      focusedTitle = document.title;
      document.title = translate('BROWSER_TAB_DEFOCUSED');
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Build the routes element
  const routesElement = (
    <RouterRoutes>
      {/* Dynamic routes from CMS */}
      {dynamicRoutes.map((route, index) => (
        <Route key={index} path={route.path} element={route.element} />
      ))}

      {/* Static routes */}
      <Route path={Constants.ROUTE_BASE} element={<StartPage />} />
      <Route path={Constants.ROUTE_PLANNER} element={<PlannerPage />} />
      <Route path={Constants.ROUTE_PROFILE_LANDING} element={<ProfileLanding />} />
      <Route path={Constants.ROUTE_JOURNEY} element={<DepartureJourneyPage />} />
      <Route path={Constants.ROUTE_RETURN_JOURNEY} element={<ReturnJourneyPage />} />
      <Route path={Constants.ROUTE_LOGIN_PROCEED} element={<LoginProceedPage />} />
      <Route path={Constants.ROUTE_REVIEW} element={<ReviewPage />} />
      <Route path={Constants.ROUTE_PAYMENT} element={<PaymentPage />} />
      <Route path={Constants.ROUTE_NETS_REDIRECT} element={<NetsRedirectPage />} />
      <Route path={Constants.ROUTE_VIPPS_REDIRECT} element={<VippsRedirectPage />} />
      <Route path={Constants.ROUTE_CREATE_PROFILE} element={<ProfilePage />} />
      <Route path={Constants.ROUTE_EDIT_PROFILE} element={<ProfilePage />} />
      <Route path={Constants.ROUTE_CHANGE_PASSWORD} element={<ChangePasswordPage />} />
      <Route path={Constants.ROUTE_PROFILE_DATA} element={<ProfileDataPage />} />
      <Route path={Constants.ROUTE_DELETE_PROFILE} element={<ProfileDeletePage />} />
      <Route path={Constants.ROUTE_MY_JOURNEYS} element={<MyJourneysPage />} />
      <Route path={Constants.ROUTE_MY_CARDS} element={<MyCardsPage />} />
      <Route path={Constants.ROUTE_RESPONSE_PARAM} element={<ResponsePage type="" />} />
      <Route path={Constants.ROUTE_CMS_LEGAL_SLUG_OLD} element={<ContentPage slug="" />} />
      <Route path={Constants.ROUTE_CMS_PREVIEW_SLUG} element={<PreviewPage slug="" />} />
      <Route path="/rtv" element={<RealTimeView />} />
      <Route path={Constants.ROUTE_SPENN_BONUS} element={<BonusPage />} />

      <Route path="*" element={<Navigate to={Constants.ROUTE_BASE} replace />} />
    </RouterRoutes>
  );

  return children({
    routesLoading,
    cmsLinks,
    headerLinks,
    footerLinks,
    onRouteChangeFocusRef,
    routesElement,
  });
};
